import {observer} from 'mobx-react-lite'
import React, {useCallback} from 'react'
import {Lang} from 'yougile-lang'
import {Time, TimeInterval, Util} from 'yougile-util'
import {
  SystemStickerType,
  TimerStickerEntry,
  TimerStickerValue
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {renderStateful, StatefulData} from '@components/stateful'
import {StopwatchColumnContent} from '@components/sticker/my-stickers/board-stickers/sticker-settings/stopwatch/stopwatch-column-content'
import {StickerMenuHeader} from '@components/sticker/sticker-menu/sticker-menu-header'
import {useStickerStore} from '@components/sticker/sticker-store'
import {UserAvatar} from '@components/user-avatar'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'

export const StickerMenuStopwatchTask = observer((): JSX.Element => {
  const [state, store] = useStickerStore()

  const canEdit =
    !store.stickerTask ||
    PermissionsChecker.checkEditTaskStickers(
      store.stickerTask,
      store.sticker,
      undefined
    )

  const value = state.value as TimerStickerValue
  const {running} = value?.current || {}
  const task = store.stickerTask
  const board = task && Task.getBoardFromTask(task)
  const isStickerActiveInBoard = Boolean(
    board && Sticker.isActiveInBoard(board, store.sticker)
  )
  const isTimer = store.stickerData.stickerType === 'timer'
  const valueIsSet = Boolean(state.value)

  const toggleClass =
    'sticker-timer-edit-tl-toggle-icon fa ' + (running ? 'fa-pause' : 'fa-play')

  const langStart = isTimer ? Lang.msg.startTimer : Lang.msg.startStopwatch
  const langStop = isTimer ? Lang.msg.stopTimer : Lang.msg.stopStopwatch

  const toggleText = running ? langStop : langStart

  const toggle = () => {
    if (canEdit) {
      toggleTimer({
        isTimer,
        defaultValue: state.defaultValue as TimerStickerValue,
        setStickerValue: store.setDefaultValue,
        pinStickerValue: store.pinStickerValue,
        setIsRunningTimer: store.setIsRunningTimer,
        closeStickerMenu: store.closeStickerMenu
      })
    }
  }

  const onSetInitialTime = useCallback(
    (value: TimerStickerValue) => {
      const initial = state.value as TimerStickerValue
      const history = initial ? initial.history || [] : []
      if (canEdit) {
        store.setStickerValue({
          ...value,
          history: [...history, value.current]
        })
        store.pinStickerValue()
        store.setIsRunningTimer(true)
      }
    },
    [canEdit]
  )

  return (
    <div className="min-w-[260px]">
      <div className="sticker-item-list-head flex">
        <StickerMenuHeader
          stickerId={store.sticker.id}
          title={
            Lang.msg.stickerNames[
              store.stickerData.stickerType as SystemStickerType
            ]
          }
          showSettings={false}
          showUnpin={canEdit}
        />
      </div>
      <div className="sticker-item-list-body">
        <div className="sticker-timer-edit">
          {(!isTimer || valueIsSet) && (
            <div className="sticker-timer-edit-tl">
              <div
                className={cn(
                  'sticker-timer-edit-tl-toggle',
                  !canEdit && 'disabled'
                )}
                onClick={toggle}
              >
                <i className={toggleClass} /> {toggleText}
              </div>

              <StopsTimeHint value={value} />
              <Timeline value={value} />

              {!isTimer && (
                <>
                  {isStickerActiveInBoard &&
                    board?.data?.stickersSettings?.[store.sticker.id]
                      ?.shouldResetOnMove &&
                    task && (
                      <StopwatchColumnContent
                        timerStickerValue={value}
                        task={task}
                      />
                    )}
                </>
              )}
            </div>
          )}

          {isTimer && (
            <>
              <SetTime
                initialTime={(state.value as TimerStickerValue)?.current?.time}
                onSetInitialTime={onSetInitialTime}
                disabled={!canEdit}
              />
              {valueIsSet && (
                <div className="sticker-deadline-edit-hist">
                  <div className="sticker-deadline-edit-hist-head pl-[7px]">
                    {Lang.msg.historyChanges}
                  </div>
                  <WithPerfectScrollbar className="max-h-[120px] px-[7px]">
                    <History />
                  </WithPerfectScrollbar>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
})

const SetTime = React.memo(
  ({
    initialTime,
    onSetInitialTime,
    disabled
  }: {
    initialTime: string
    onSetInitialTime: (value: TimerStickerValue) => void
    disabled?: boolean
  }) => {
    let ti = initialTime
      ? TimeInterval.fromString(initialTime)
      : TimeInterval.fromString('0s')
    let changed = false

    const draw = (data: StatefulData) => {
      const change = (interval: string) => () => {
        if (disabled) {
          return
        }

        const newTi = ti.add(TimeInterval.fromString(interval))
        if (newTi.isNegative) {
          return
        }
        ti = newTi

        changed = true
        data.refresh()
      }
      const changers = (s: string) => (
        <div className="sticker-timer-edit-num">
          <div className="sticker-timer-edit-num-up" onClick={change(s)} />
          <div
            className="sticker-timer-edit-num-down"
            onClick={change('-' + s)}
          />
        </div>
      )
      const part = (value: string, title: string, range: string) => (
        <div className="sticker-timer-edit-part">
          {value} {changers(range)}
          <div className="sticker-timer-edit-sub">{title}</div>
        </div>
      )
      const save = () => {
        if (disabled) {
          return
        }

        const val: TimerStickerValue = {
          current: {
            time: ti.toString(),
            by: globals.currentUserId,
            timestamp: Time.now(),
            running: true
          },
          history: []
        }
        onSetInitialTime(val)
      }
      const editSaveClass = Util.className({
        'sticker-timer-edit-save _button': true,
        disabled: disabled || (Boolean(initialTime) && !changed)
      })
      return (
        <div className={cn('sticker-timer-edit', disabled && 'disabled')}>
          <div className="sticker-timer-edit-main">
            <div className="sticker-timer-edit-time">
              {part('' + ti.weeks, Lang.msg.weeks, '1w')}
              <div className="sticker-timer-edit-delim">/</div>
              {part('' + ti.days, Lang.msg.days, '1d')}
              <div className="sticker-timer-edit-delim">/</div>
              {part('' + ti.hours, Lang.msg.hours, '1h')}
              <div className="sticker-timer-edit-delim">:</div>
              {part(('00' + ti.minutes).slice(-2), Lang.msg.minutes, '1m')}
            </div>
            <div className={editSaveClass} onClick={save}>
              {initialTime ? Lang.msg.change : Lang.msg.start}
            </div>
          </div>
        </div>
      )
    }

    return renderStateful({}, draw)
  }
)

const Timeline = ({value}: {value: TimerStickerValue}) => {
  const parts: JSX.Element[] = []
  const timerStates =
    value?.history && value?.current ? [...value.history, value.current] : []
  timerStates.forEach((ts: TimerStickerEntry, ix: number) => {
    const time =
      timerStates.length > ix + 1
        ? timerStates[ix + 1].timestamp - ts.timestamp
        : Time.now() - ts.timestamp
    const minutes = Math.ceil(time / 60000)
    const seconds = Math.ceil(time / 1000)
    const className = Util.className({
      'sticker-timeline-part': true,
      running: ts.running
    })

    const tooltipContent = (
      <div className="text-12 w-160 p-6">
        <div className="text-grey-400">{Time.formatDateTime(ts.timestamp)}</div>
        <div className="mt-4 flex items-center">
          {ts.running ? Lang.msg.startedBy : Lang.msg.stoppedBy}
          <UserAvatar userId={ts.by} className="ml-2" />
        </div>
        <div className="mt-4 whitespace-nowrap">
          {ts.running ? Lang.msg.runTime : Lang.msg.stoppedTime}
          <span className="font-semibold ml-2">
            {TimeInterval.fromString(seconds + 's').format()}
          </span>
        </div>
      </div>
    )

    parts.push(
      <Tooltip
        content={tooltipContent}
        placement="bottom"
        key={ts.timestamp + ix}
      >
        <div className={className} style={{flex: minutes}}></div>
      </Tooltip>
    )
  })
  return <div className="sticker-timeline">{parts}</div>
}

const History = observer(() => {
  const [state] = useStickerStore()
  const value = state.value as TimerStickerValue
  const allHistory =
    value?.history && value?.current ? [...value.history, value.current] : []

  return (
    <>
      {allHistory.map((ts: TimerStickerEntry, ix: number) => {
        const oldTi = TimeInterval.fromString(ts.time)
        const prev = ix > 0 ? allHistory[ix - 1] : undefined
        const from =
          ix > 0 && prev?.time
            ? TimeInterval.fromString(prev.time)
                .add(TimeInterval.fromDates(ts.timestamp, prev.timestamp))
                .format()
            : undefined
        const txt = ix > 0 ? from + ' =>' : Lang.msg.set
        return (
          <div
            key={ts.timestamp + ix}
            className="sticker-deadline-edit-hist-item"
          >
            <div className="sticker-deadline-edit-hist-n">{ix + 1}.</div>
            <div className="sticker-deadline-edit-hist-val">
              <span>{txt}</span> {oldTi.format()}
            </div>
            <UserAvatar userId={ts.by} />
          </div>
        )
      })}
    </>
  )
})

interface ToggleTimer {
  isTimer: boolean
  defaultValue: TimerStickerValue
  setStickerValue: (value: TimerStickerValue) => void
  pinStickerValue: () => void
  setIsRunningTimer: (value: boolean) => void
  closeStickerMenu: () => void
}

const toggleTimer = ({
  isTimer,
  defaultValue,
  setStickerValue,
  pinStickerValue,
  setIsRunningTimer,
  closeStickerMenu
}: ToggleTimer) => {
  const history = defaultValue.history || []
  if (defaultValue?.current) {
    history.push(defaultValue.current)
  }
  const oldTi = TimeInterval.fromString(defaultValue.current.time)
  const newTi = oldTi.add(
    TimeInterval.fromDates(
      isTimer ? Time.now() : defaultValue.current.timestamp,
      isTimer ? defaultValue.current.timestamp : Time.now()
    )
  )
  const isRunning = !defaultValue.current.running

  setStickerValue({
    current: {
      time: !isRunning ? newTi.toString() : oldTi.toString(),
      by: globals.currentUserId,
      timestamp: Time.now(),
      running: isRunning
    },
    history
  })
  pinStickerValue()
  setIsRunningTimer(isRunning)
  closeStickerMenu()
}

const StopsTimeHint = ({value}: {value: TimerStickerValue}) => {
  let stopsTimeSec = 0
  const allHistory =
    value?.history && value?.current ? [...value.history, value.current] : []

  allHistory.forEach((ts: TimerStickerEntry, ix: number) => {
    if (!ts.running) {
      const time =
        allHistory.length > ix + 1
          ? allHistory[ix + 1].timestamp - ts.timestamp
          : Time.now() - ts.timestamp
      stopsTimeSec += Math.ceil(time / 1000)
    }
  })

  if (!stopsTimeSec) {
    return <></>
  }

  return (
    <div className="sticker-timer-edit-tl-stops">
      {Lang.msg.totalStopsTime}
      <div className="sticker-timer-edit-tl-stops-val">
        {TimeInterval.fromString(stopsTimeSec + 's').format()}
      </div>
    </div>
  )
}
